if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.dynamicCheckoutButton = this.querySelector('.shopify-payment-button');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
 
        if (this.form) {
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        }

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.onDocumentChangeHandler = this.onDocumentChange.bind(this);
        document.addEventListener('change', this.onDocumentChangeHandler, true);

        this.syncChooseOptionState();
      }

      connectedCallback() {
        this.syncChooseOptionState();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onDocumentChangeHandler, true);
      }

      getVariantSelects() {
        return (
          this.closest('product-info')?.querySelector(`variant-selects[data-section="${this.dataset.sectionId}"]`) ||
          document.querySelector(`variant-selects[data-section="${this.dataset.sectionId}"]`)
        );
      }

      getVariantDropdowns() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return [];
        return Array.from(variantSelects.querySelectorAll('select'));
      }

      isChooseOptionSelected() {
        const dropdowns = this.getVariantDropdowns();
        if (!dropdowns.length) return false;

        return dropdowns.some((select, index) => {
          const selectedOption = select.options[select.selectedIndex];
          if (!selectedOption) return false;

          const selectedText = (selectedOption.textContent || '').trim().toLowerCase();
          const selectedValue = (select.value || '').trim().toLowerCase();

          return (
            selectedValue === '' ||
            selectedText === 'choose an option' ||
            (index === 0 && selectedText === 'select an option')
          );
        });
      }

      // Reads the data-available flag rendered by buy-buttons.liquid so this
      // script never force-enables a button for a variant that is actually sold out.
      isVariantAvailable() {
        if (!this.submitButton) return true;
        return this.submitButton.dataset.available !== 'false';
      }

      onDocumentChange(event) {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return;
        if (!variantSelects.contains(event.target)) return;

        this.syncChooseOptionState();

        requestAnimationFrame(() => {
          this.syncChooseOptionState();
        });

        setTimeout(() => {
          this.syncChooseOptionState();
        }, 0);
      }

      disableForChooseOption() {
        if (this.variantIdInput) {
          this.variantIdInput.value = '';
          this.variantIdInput.disabled = true;
          this.variantIdInput.setAttribute('disabled', 'disabled');
        }

        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.remove('loading');
        }

        if (this.submitButtonText) {
          this.submitButtonText.classList.remove('hidden');
          this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        }

        const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
        if (soldOutMessage) {
          soldOutMessage.classList.add('hidden');
        }

        const spinner = this.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.add('hidden');
        }

        if (this.dynamicCheckoutButton) {
          this.dynamicCheckoutButton.hidden = true;
          this.dynamicCheckoutButton.setAttribute('hidden', 'hidden');
          this.dynamicCheckoutButton.setAttribute('aria-hidden', 'true');
          this.dynamicCheckoutButton.style.display = 'none';
          this.dynamicCheckoutButton.style.pointerEvents = 'none';
        }
      }

      enableAfterValidSelection() {
        // An option is selected, but that doesn't mean it's purchasable.
        // Check real availability before touching disabled state or button text.
        const isAvailable = this.isVariantAvailable();

        if (this.variantIdInput) {
          if (isAvailable) {
            this.variantIdInput.disabled = false;
            this.variantIdInput.removeAttribute('disabled');
          } else {
            this.variantIdInput.disabled = true;
            this.variantIdInput.setAttribute('disabled', 'disabled');
          }
        }

        if (this.submitButton && !this.error) {
          if (isAvailable) {
            this.submitButton.disabled = false;
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
          } else {
            this.submitButton.disabled = true;
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButton.setAttribute('aria-disabled', 'true');
          }
        }

        if (this.submitButtonText) {
          this.submitButtonText.classList.remove('hidden');
          this.submitButtonText.textContent = isAvailable
            ? (window.variantStrings?.addToCart || 'Add to cart')
            : (window.variantStrings?.soldOut || 'Sold out');
        }

        const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
        if (soldOutMessage) {
          soldOutMessage.classList.add('hidden');
        }

        if (this.dynamicCheckoutButton) {
          if (isAvailable) {
            this.dynamicCheckoutButton.hidden = false;
            this.dynamicCheckoutButton.removeAttribute('hidden');
            this.dynamicCheckoutButton.removeAttribute('aria-hidden');
            this.dynamicCheckoutButton.style.display = '';
            this.dynamicCheckoutButton.style.pointerEvents = '';
          } else {
            this.dynamicCheckoutButton.hidden = true;
            this.dynamicCheckoutButton.setAttribute('hidden', 'hidden');
            this.dynamicCheckoutButton.setAttribute('aria-hidden', 'true');
            this.dynamicCheckoutButton.style.display = 'none';
            this.dynamicCheckoutButton.style.pointerEvents = 'none';
          }
        }
      }

      syncChooseOptionState() {
        this.dynamicCheckoutButton = this.querySelector('.shopify-payment-button');

        if (this.isChooseOptionSelected()) {
          this.disableForChooseOption();
        } else {
          this.enableAfterValidSelection();
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.isChooseOptionSelected()) {
          this.syncChooseOptionState();
          return;
        }

        // Block submission client-side too if the variant isn't available.
        if (!this.isVariantAvailable()) {
          this.syncChooseOptionState();
          return;
        }

        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
        }

        const spinner = this.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
              if (!soldOutMessage) return;

              this.submitButton?.setAttribute('aria-disabled', 'true');

              if (this.submitButtonText) {
                this.submitButtonText.classList.add('hidden');
              }

              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) {
              this.submitButton.classList.remove('loading');
            }

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            const spinner = this.querySelector('.loading__spinner');
            if (spinner) {
              spinner.classList.add('hidden');
            }

            // Don't blindly re-enable here — let syncChooseOptionState
            // (called right below) decide based on real availability.
            if (!this.error && !this.isChooseOptionSelected() && this.submitButton && this.isVariantAvailable()) {
              this.submitButton.disabled = false;
              this.submitButton.removeAttribute('disabled');
              this.submitButton.removeAttribute('aria-disabled');
            }

            this.syncChooseOptionState();

            requestAnimationFrame(() => {
              this.syncChooseOptionState();
            });

            setTimeout(() => {
              this.syncChooseOptionState();
            }, 0);

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.isChooseOptionSelected()) {
          this.disableForChooseOption();
          return;
        }

        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');

          if (text && this.submitButtonText) {
            this.submitButtonText.textContent = text;
          }
        } else {
          // Only re-enable if the variant is actually available.
          const isAvailable = this.isVariantAvailable();

          this.submitButton.disabled = !isAvailable;
          if (isAvailable) {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
          } else {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButton.setAttribute('aria-disabled', 'true');
          }

          if (this.submitButtonText) {
            this.submitButtonText.textContent = isAvailable
              ? (window.variantStrings?.addToCart || 'Add to cart')
              : (window.variantStrings?.soldOut || 'Sold out');
          }
        }

        this.syncChooseOptionState();
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector('[name="id"]') : null;
      }
    }
  );
}